import React, { useEffect, useState } from 'react';
import { Card, Radio, Typography, Space, Alert, Row, Col, Tag, Divider } from 'antd';
import { ClockCircleOutlined, StarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { PlanType, ValidationError } from '../../../../types/plan-template';

const { Title, Paragraph, Text } = Typography;

interface PlanTypeSelectionProps {
  planType?: PlanType;
  onPlanTypeChange: (planType: PlanType) => void;
  onValidationChange: (isValid: boolean, errors: ValidationError[]) => void;
  onNext: () => void;
  onPrevious: () => void;
}



const getComplexityColor = (complexity: string) => {
  switch (complexity) {
    case 'low': return '#52c41a';
    case 'medium': return '#faad14';
    case 'high': return '#f5222d';
    default: return '#d9d9d9';
  }
};

const PlanTypeSelection: React.FC<PlanTypeSelectionProps> = ({
  planType,
  onPlanTypeChange,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<PlanType | undefined>(planType);

  const getComplexityText = (complexity: string) => {
    switch (complexity) {
      case 'low': return t('planTemplate.wizard.planTypeSelection.complexityLevels.simple');
      case 'medium': return t('planTemplate.wizard.planTypeSelection.complexityLevels.medium');
      case 'high': return t('planTemplate.wizard.planTypeSelection.complexityLevels.complex');
      default: return t('common.unknown');
    }
  };

  // Dynamic plan type options with internationalization
  const PLAN_TYPE_OPTIONS: Array<{
    value: PlanType;
    label: string;
    description: string;
    features: string[];
    useCases: string[];
    duration: string;
    complexity: 'low' | 'medium' | 'high';
  }> = [
    {
      value: 'Major Plans',
      label: t('planTemplate.wizard.planTypeSelection.types.majorPlans.label'),
      description: t('planTemplate.wizard.planTypeSelection.types.majorPlans.description'),
      features: t('planTemplate.wizard.planTypeSelection.types.majorPlans.features', { returnObjects: true }) as string[],
      useCases: t('planTemplate.wizard.planTypeSelection.types.majorPlans.useCases', { returnObjects: true }) as string[],
      duration: t('planTemplate.wizard.planTypeSelection.types.majorPlans.duration'),
      complexity: 'high'
    },
    {
      value: 'Regular Plans',
      label: t('planTemplate.wizard.planTypeSelection.types.regularPlans.label'),
      description: t('planTemplate.wizard.planTypeSelection.types.regularPlans.description'),
      features: t('planTemplate.wizard.planTypeSelection.types.regularPlans.features', { returnObjects: true }) as string[],
      useCases: t('planTemplate.wizard.planTypeSelection.types.regularPlans.useCases', { returnObjects: true }) as string[],
      duration: t('planTemplate.wizard.planTypeSelection.types.regularPlans.duration'),
      complexity: 'medium'
    },
    {
      value: 'Minor Plans',
      label: t('planTemplate.wizard.planTypeSelection.types.minorPlans.label'),
      description: t('planTemplate.wizard.planTypeSelection.types.minorPlans.description'),
      features: t('planTemplate.wizard.planTypeSelection.types.minorPlans.features', { returnObjects: true }) as string[],
      useCases: t('planTemplate.wizard.planTypeSelection.types.minorPlans.useCases', { returnObjects: true }) as string[],
      duration: t('planTemplate.wizard.planTypeSelection.types.minorPlans.duration'),
      complexity: 'low'
    },
    {
      value: 'Urgent Plans',
      label: t('planTemplate.wizard.planTypeSelection.types.urgentPlans.label'),
      description: t('planTemplate.wizard.planTypeSelection.types.urgentPlans.description'),
      features: t('planTemplate.wizard.planTypeSelection.types.urgentPlans.features', { returnObjects: true }) as string[],
      useCases: t('planTemplate.wizard.planTypeSelection.types.urgentPlans.useCases', { returnObjects: true }) as string[],
      duration: t('planTemplate.wizard.planTypeSelection.types.urgentPlans.duration'),
      complexity: 'medium'
    },
    {
      value: 'Special Plans',
      label: t('planTemplate.wizard.planTypeSelection.types.specialPlans.label'),
      description: t('planTemplate.wizard.planTypeSelection.types.specialPlans.description'),
      features: t('planTemplate.wizard.planTypeSelection.types.specialPlans.features', { returnObjects: true }) as string[],
      useCases: t('planTemplate.wizard.planTypeSelection.types.specialPlans.useCases', { returnObjects: true }) as string[],
      duration: t('planTemplate.wizard.planTypeSelection.types.specialPlans.duration'),
      complexity: 'high'
    }
  ];

  useEffect(() => {
    // Validate selection
    const isValid = !!selectedType;
    const errors: ValidationError[] = [];

    if (!selectedType) {
      errors.push({
        field: 'planType',
        message: t('planTemplate.wizard.planTypeSelection.selectPlanTypeValidation'),
        type: 'error'
      });
    }

    onValidationChange(isValid, errors);
  }, [selectedType, onValidationChange]);

  const handleTypeChange = (value: PlanType) => {
    setSelectedType(value);
    onPlanTypeChange(value);
  };

  const selectedOption = PLAN_TYPE_OPTIONS.find(option => option.value === selectedType);

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <Title level={3}>{t('planTemplate.wizard.planTypeSelection.title')}</Title>
        <Paragraph type="secondary">
          {t('planTemplate.wizard.planTypeSelection.description')}
        </Paragraph>
      </div>

      <Radio.Group
        value={selectedType}
        onChange={(e) => handleTypeChange(e.target.value)}
        style={{ width: '100%' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {PLAN_TYPE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value} style={{ width: '100%' }}>
              <Card
                hoverable
                style={{ 
                  width: '100%',
                  marginLeft: '24px',
                  border: selectedType === option.value ? '2px solid #1890ff' : '1px solid #e8e8e8',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedType === option.value 
                    ? '0 4px 20px rgba(24, 144, 255, 0.15)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.06)',
                }}
                styles={{ 
                  body: {
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '20px'
                  }
                }}
              >
                {/* 左侧主要信息区域 */}
                <div style={{ flex: '1 1 60%', minWidth: 0 }}>
                  {/* 标题行 */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginBottom: '6px',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <Title level={4} style={{ 
                      margin: 0, 
                      color: selectedType === option.value ? '#1890ff' : '#262626',
                      fontSize: '18px',
                      fontWeight: 600
                    }}>
                      {option.label}
                    </Title>
                    <Tag 
                      color={getComplexityColor(option.complexity)}
                      style={{ 
                        borderRadius: '4px',
                        fontWeight: 500,
                        fontSize: '11px',
                        margin: 0
                      }}
                    >
                      {getComplexityText(option.complexity)}
                    </Tag>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#666',
                      fontSize: '13px'
                    }}>
                      <ClockCircleOutlined style={{ marginRight: '4px' }} />
                      <span>{option.duration}</span>
                    </div>
                  </div>
                  
                  {/* 描述 */}
                  <Paragraph style={{ 
                    margin: '0 0 12px 0',
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.4'
                  }}>
                    {option.description}
                  </Paragraph>
                </div>

                {/* 右侧特点和场景区域 */}
                <div style={{ 
                  flex: '1 1 40%',
                  display: 'flex',
                  gap: '20px',
                  minWidth: 0
                }}>
                  {/* 主要特点 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '8px',
                      color: '#262626',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      <StarOutlined style={{ marginRight: '4px', color: '#faad14', fontSize: '12px' }} />
                      {t('planTemplate.wizard.planTypeSelection.planTypeDetails.features')}
                    </div>
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      {option.features.slice(0, 3).map((feature, index) => (
                        <div key={index} style={{ 
                          color: '#666', 
                          marginBottom: '3px',
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          <span style={{ 
                            color: '#52c41a',
                            marginRight: '4px',
                            fontSize: '10px',
                            marginTop: '1px',
                            flexShrink: 0
                          }}>•</span>
                          <span style={{ wordBreak: 'break-word' }}>{feature}</span>
                        </div>
                      ))}
                      {option.features.length > 3 && (
                        <div style={{ 
                          color: '#999', 
                          fontSize: '11px',
                          fontStyle: 'italic',
                          marginTop: '2px'
                        }}>
                          +{option.features.length - 3} 更多...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 适用场景 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '8px',
                      color: '#262626',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      <CheckCircleOutlined style={{ marginRight: '4px', color: '#52c41a', fontSize: '12px' }} />
                      {t('planTemplate.wizard.planTypeSelection.planTypeDetails.useCases')}
                    </div>
                    <div style={{ 
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px'
                    }}>
                      {option.useCases.slice(0, 4).map((useCase, index) => (
                        <Tag 
                          key={index}
                          style={{ 
                            fontSize: '11px',
                            borderRadius: '3px',
                            border: 'none',
                            backgroundColor: selectedType === option.value ? '#e6f7ff' : '#f6f8fa',
                            color: selectedType === option.value ? '#1890ff' : '#666',
                            margin: 0,
                            padding: '2px 6px',
                            lineHeight: '1.2'
                          }}
                        >
                          {useCase}
                        </Tag>
                      ))}
                      {option.useCases.length > 4 && (
                        <Tag style={{ 
                          fontSize: '11px',
                          borderRadius: '3px',
                          border: 'none',
                          backgroundColor: '#f0f0f0',
                          color: '#999',
                          margin: 0,
                          padding: '2px 6px',
                          lineHeight: '1.2'
                        }}>
                          +{option.useCases.length - 4}
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </Radio>
          ))}
        </div>
      </Radio.Group>

      {selectedOption && (
        <Alert
          style={{ 
            marginTop: '32px',
            borderRadius: '8px',
            border: '1px solid #d4edda',
            backgroundColor: '#f8f9fa'
          }}
          message={
            <span style={{ fontWeight: 500 }}>
              {t('common.selected')}: {selectedOption.label}
            </span>
          }
          description={
            <div>
              <div style={{ marginBottom: '4px' }}>
                {selectedOption.description}
              </div>
              <div style={{ color: '#666', fontSize: '13px' }}>
                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                {t('planTemplate.wizard.planTypeSelection.planTypeDetails.duration')}: {selectedOption.duration}
              </div>
            </div>
          }
          type="info"
          showIcon
        />
      )}
    </div>
  );
};

export default PlanTypeSelection;